/**
 * "Resulting figures" for step 2 (f10.5, design D7): the country × month matrix
 * as the server computed it on this read. Gaps read "—", excluded columns are
 * struck through, undecided ones greyed. **There is no total column and no
 * total row** (Q-f10.5-6): nothing here adds anything up.
 */

import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

import type { BillableDaysFigures } from '@/api/client';

import { MONTH_LABELS, matrixRowClass } from './billableDaysView';

function Stat({ label, value, foot }: { label: string; value: number; foot: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value.toLocaleString()}</div>
      <div className="stat-foot">{foot}</div>
    </div>
  );
}

export function BillableDaysFiguresCard({ figures, footer }: { figures: BillableDaysFigures; footer?: ReactNode }) {
  const { coverage } = figures;
  const notCovered = figures.legacy_note.not_covered_by_legacy_lookup;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Resulting figures</h3>
        <span className="hint">Billable days per FTE · {figures.period_year} · updated after every decision</span>
      </div>
      <div className="card-body stack">
        <div className="stat-grid">
          <Stat
            label="Country columns"
            value={coverage.columns}
            foot={
              <>
                {coverage.matched} matched &middot; {coverage.mapped} mapped &middot; {coverage.excluded} excluded
                &middot; {coverage.undecided} undecided
              </>
            }
          />
          <Stat label="Values to commit" value={coverage.values_to_commit} foot="One per country and month" />
          <Stat label="Missing cells" value={coverage.missing_cells} foot="Shown as — ; 0 headcount downstream" />
          <Stat
            label="Changed vs committed"
            value={figures.previous?.changed_cells ?? 0}
            foot={figures.previous ? 'Cells a re-accept will change' : 'Nothing committed yet'}
          />
        </div>

        <div className="table-scroll">
          <table className="data compact bd-matrix">
            <thead>
              <tr>
                <th>Country</th>
                {figures.months.map((month) => (
                  <th key={month} className="num">
                    {MONTH_LABELS[month - 1]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {figures.countries.map((row) => (
                <tr key={row.staged_country_id} className={matrixRowClass(row.decision)}>
                  <td>
                    <span className="us-eng-strong">{row.country?.code ?? row.raw_country_name}</span>{' '}
                    <span className="hint">{row.country ? row.country.name : 'unmatched'}</span>
                    {row.missing_months.length > 0 && (
                      <span className="badge pill-warning bd-gap-badge" title="Months without a value">
                        <AlertTriangle aria-hidden="true" /> {row.missing_months.length}
                      </span>
                    )}
                  </td>
                  {figures.months.map((month) => {
                    const value = row.values[String(month)];
                    return (
                      <td key={month} className="num mono">
                        {value ?? <span className="bd-gap">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {figures.countries_without_days.length > 0 && (
          <p className="hint">
            No billable days in this feed: {figures.countries_without_days.map((c) => c.name).join(', ')}
          </p>
        )}
        {notCovered.length > 0 && (
          <div className="alert alert-info">
            <div className="a-body">
              The legacy lookup did not cover {notCovered.join(', ')} &mdash; their headcount used to fall to
              zero. Atlas stores and serves them like every other country.
            </div>
          </div>
        )}
      </div>
      {footer}
    </div>
  );
}
